// Auto-translate: fills the localized texts and the fields of the language
// the editor is switched to from the default one, and only what is still
// empty — it never writes over a translation somebody made.
//
// It sits next to the language it translates into: the picker at the top of
// the product dialog, which every tab follows.
import React, { useState } from "react"
import Button from "aldoo-ra/Button"
import { LanguageIcon } from "@heroicons/react/24/outline"
import { showNotification } from "aldoo-ra/Notification"
import { AutoTranslate } from "../../../AdminShared/api"
import { errorMessage } from "../../../AdminShared/ui"
import { AUTO_TRANSLATION_FIELDS } from "./editor-config"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"

const notify = (message, isError = false) =>
  showNotification({ message, className: isError ? "bg-admin_error text-white" : "bg-admin_success text-white" })

export default function AutoTranslateButton({ editor, className = "" }) {
  const t = useProductEditorTexts()
  const { product, read, set, language, defaultLanguage, loc, setText } = editor
  const [translating, setTranslating] = useState(false)
  const isDefault = language === defaultLanguage

  const translateAll = async () => {
    if (isDefault) return notify(t`Switch to a language other than the default one`, true)
    setTranslating(true)
    try {
      let translated = 0
      for (const field of AUTO_TRANSLATION_FIELDS) {
        const id = product?.[field]
        const entry = loc[typeof id === "object" ? id?._id : id]
        if (!entry || entry.type !== "text" || entry.translations?.[language] || !entry.translations?.[defaultLanguage]) continue
        const res = await AutoTranslate(entry.translations[defaultLanguage], defaultLanguage, language)
        if (errorMessage(res)) return notify(res.message || errorMessage(res), true)
        setText(id, res.text)
        translated++
      }
      // the product's fields: a text is translated, a number is copied
      const fieldsId = product?.fields
      const fieldsEntry = fieldsId ? loc[typeof fieldsId === "object" ? fieldsId?._id : fieldsId] : null
      const source = fieldsEntry?.translations?.[defaultLanguage]
      if (source && typeof source === "object") {
        const target = { ...(fieldsEntry.translations?.[language] || {}) }
        let changed = false
        for (const [key, value] of Object.entries(source)) {
          if (target[key] || value === undefined || value === null || value === "") continue
          if (/^[\d.,\s]+$/.test(String(value))) {
            target[key] = value
          } else {
            const res = await AutoTranslate(String(value), defaultLanguage, language)
            if (errorMessage(res)) return notify(res.message || errorMessage(res), true)
            target[key] = res.text
          }
          changed = true
          translated++
        }
        if (changed) set("fields", target)
      }
      notify(translated ? t(translated === 1 ? "Translated %s field to %s" : "Translated %s fields to %s", translated, language.toUpperCase()) : t`Nothing to translate`)
    } finally {
      setTranslating(false)
    }
  }

  return (
    <Button
      variant="admin_secondary"
      className={`px-3 h-[38px] text-xs ${className}`}
      onClick={translateAll}
      disabled={translating || isDefault}
      title={isDefault ? t`Switch to a language other than the default one` : t("Fill the empty %s texts from %s", language.toUpperCase(), defaultLanguage.toUpperCase())}
      data-testid="auto-translate"
    >
      <LanguageIcon className="w-4 h-4" />
      {translating ? t`Translating…` : t`Auto-translate`}
    </Button>
  )
}
